const API_URL = process.env.NEXT_PUBLIC_API_URL!;

// --- Types ---
export interface JobSubmitResponse {
  job_id: string;
  status: string;
}

export interface JobStatusResponse {
  job_id: string;
  status: "processing" | "completed" | "failed";
  processed_url?: string;
  original_url?: string;
  error?: string;
}

export interface BatchJobItem {
  job_id: string;
  filename: string;
}

export interface BatchSubmitResponse {
  jobs: BatchJobItem[];
}

export interface BatchStatusResponse {
  jobs: JobStatusResponse[];
}

// --- Synchronous remove background (legacy) ---
export async function removeBackground(
  file: File,
  token: string,
  width?: number,
  height?: number,
  backgroundColor?: string
): Promise<{ blob: Blob; originalUrl: string; processedUrl: string }> {
  const formData = new FormData();
  formData.append("file", file);
  if (width && height) {
    formData.append("width", width.toString());
    formData.append("height", height.toString());
  }
  if (backgroundColor) {
    formData.append("background_color", backgroundColor);
  }

  const res = await fetch(`${API_URL}/remove-bg?sync=true`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Processing failed" }));
    throw new Error(err.detail || "Processing failed");
  }

  const blob = await res.blob();
  const originalUrl = res.headers.get("X-Original-Url") || "";
  const processedUrl = res.headers.get("X-Processed-Url") || "";

  return { blob, originalUrl, processedUrl };
}

// --- Async remove background ---
export async function removeBackgroundAsync(
  file: File,
  token: string,
  width?: number,
  height?: number,
  backgroundColor?: string,
  outputFormat?: string
): Promise<JobSubmitResponse> {
  const formData = new FormData();
  formData.append("file", file);
  if (width && height) {
    formData.append("width", width.toString());
    formData.append("height", height.toString());
  }
  if (backgroundColor) {
    formData.append("background_color", backgroundColor);
  }
  if (outputFormat) {
    formData.append("output_format", outputFormat);
  }

  const res = await fetch(`${API_URL}/remove-bg`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Processing failed" }));
    throw new Error(err.detail || "Processing failed");
  }

  return (await res.json()) as JobSubmitResponse;
}

// --- Poll job status ---
export async function getJobStatus(
  jobId: string,
  token: string
): Promise<JobStatusResponse> {
  const res = await fetch(`${API_URL}/jobs/${jobId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to check job status");
  return (await res.json()) as JobStatusResponse;
}

// --- Bulk upload ---
export async function removeBackgroundBulk(
  files: File[],
  token: string,
  width?: number,
  height?: number,
  backgroundColor?: string,
  outputFormat?: string
): Promise<BatchSubmitResponse> {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  if (width && height) {
    formData.append("width", width.toString());
    formData.append("height", height.toString());
  }
  if (backgroundColor) {
    formData.append("background_color", backgroundColor);
  }
  if (outputFormat) {
    formData.append("output_format", outputFormat);
  }

  const res = await fetch(`${API_URL}/remove-bg/bulk`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Bulk upload failed" }));
    throw new Error(err.detail || "Bulk upload failed");
  }

  return (await res.json()) as BatchSubmitResponse;
}

// --- Batch job status ---
export async function getBatchJobStatus(
  jobIds: string[],
  token: string
): Promise<BatchStatusResponse> {
  const res = await fetch(`${API_URL}/jobs/batch-status`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ job_ids: jobIds }),
  });
  if (!res.ok) throw new Error("Failed to check batch status");
  return (await res.json()) as BatchStatusResponse;
}

// --- Existing endpoints ---
export async function fetchImages(token: string) {
  const res = await fetch(`${API_URL}/images`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch images");
  const data = await res.json();
  return data.images as Array<{
    id: string;
    original_url: string;
    processed_url: string;
    created_at: string;
  }>;
}

export async function fetchUsage(token: string) {
  const res = await fetch(`${API_URL}/usage`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch usage");
  return (await res.json()) as {
    images_used: number;
    limit: number;
    tier: string;
    email_notifications: boolean;
    is_admin: boolean;
  };
}

export async function createCheckoutSession(tier: string, token: string) {
  const res = await fetch(`${API_URL}/create-checkout-session`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ tier }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Failed to create checkout session" }));
    throw new Error(err.detail || "Failed to create checkout session");
  }
  return (await res.json()) as { url: string };
}

export async function downloadImagesZip(imageIds: string[], token: string): Promise<Blob> {
  const res = await fetch(`${API_URL}/images/download-zip`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ image_ids: imageIds }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Failed to create ZIP" }));
    throw new Error(err.detail || "Failed to create ZIP");
  }
  return res.blob();
}

export async function updateNotificationPreferences(token: string, enabled: boolean) {
  const res = await fetch(`${API_URL}/notifications/preferences`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ enabled }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`Failed to update notification preferences: ${res.status} ${body}`);
  }
  return (await res.json()) as { email_notifications: boolean };
}

// --- Analytics ---

export interface ApiUsageStats {
  total_requests: number;
  success_count: number;
  failure_count: number;
  avg_response_time_ms: number;
  daily_usage: { date: string; count: number }[];
}

export interface ApiUsageByKey {
  keys: {
    api_key_id: string | null;
    request_count: number;
    key_name: string | null;
    key_prefix?: string;
  }[];
}

export async function fetchApiUsage(
  token: string,
  days?: number,
  apiKeyId?: string
): Promise<ApiUsageStats> {
  const params = new URLSearchParams();
  if (days) params.set("days", days.toString());
  if (apiKeyId) params.set("api_key_id", apiKeyId);
  const res = await fetch(`${API_URL}/analytics/api-usage?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch analytics");
  return (await res.json()) as ApiUsageStats;
}

export async function fetchApiUsageByKey(
  token: string,
  days?: number
): Promise<ApiUsageByKey> {
  const params = new URLSearchParams();
  if (days) params.set("days", days.toString());
  const res = await fetch(`${API_URL}/analytics/api-usage/by-key?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch analytics by key");
  return (await res.json()) as ApiUsageByKey;
}

// --- Admin ---

export interface AdminMetrics {
  total_users: number;
  tier_breakdown: Record<string, number>;
  images_this_month: number;
  all_time_images: number;
}

export interface AdminUser {
  id: string;
  email: string;
  subscription_tier: string;
  images_used_this_month: number;
  is_admin: boolean;
  created_at: string;
}

export async function fetchAdminMetrics(token: string): Promise<AdminMetrics> {
  const res = await fetch(`${API_URL}/admin/metrics`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch admin metrics");
  return (await res.json()) as AdminMetrics;
}

export async function fetchAdminUsers(
  token: string,
  page = 1,
  search?: string
): Promise<{ users: AdminUser[]; total: number }> {
  const params = new URLSearchParams({ page: page.toString() });
  if (search) params.set("search", search);
  const res = await fetch(`${API_URL}/admin/users?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch admin users");
  return (await res.json()) as { users: AdminUser[]; total: number };
}

export async function updateUserTier(token: string, userId: string, tier: string) {
  const res = await fetch(`${API_URL}/admin/users/${userId}/tier`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ tier }),
  });
  if (!res.ok) throw new Error("Failed to update user tier");
  return res.json();
}

export async function toggleUserAdmin(token: string, userId: string, isAdmin: boolean) {
  const res = await fetch(`${API_URL}/admin/users/${userId}/admin`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ is_admin: isAdmin }),
  });
  if (!res.ok) throw new Error("Failed to toggle admin status");
  return res.json();
}

// --- Webhooks ---

export interface WebhookItem {
  id: string;
  url: string;
  events: string[];
  active: boolean;
  created_at: string;
  secret?: string;
}

export interface WebhookDeliveryItem {
  id: string;
  event_type: string;
  status_code: number | null;
  attempt: number;
  created_at: string;
}

export async function fetchWebhooks(token: string): Promise<WebhookItem[]> {
  const res = await fetch(`${API_URL}/webhooks`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch webhooks");
  const data = await res.json();
  return data.webhooks as WebhookItem[];
}

export async function createWebhook(
  token: string,
  url: string,
  events: string[]
): Promise<WebhookItem> {
  const res = await fetch(`${API_URL}/webhooks`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ url, events }),
  });
  if (!res.ok) throw new Error("Failed to create webhook");
  return (await res.json()) as WebhookItem;
}

export async function deleteWebhook(token: string, webhookId: string) {
  const res = await fetch(`${API_URL}/webhooks/${webhookId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to delete webhook");
  return res.json();
}

export async function fetchWebhookDeliveries(
  token: string,
  webhookId: string
): Promise<WebhookDeliveryItem[]> {
  const res = await fetch(`${API_URL}/webhooks/${webhookId}/deliveries`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch deliveries");
  const data = await res.json();
  return data.deliveries as WebhookDeliveryItem[];
}

export async function createPortalSession(token: string) {
  const res = await fetch(`${API_URL}/create-portal-session`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Failed to create portal session" }));
    throw new Error(err.detail || "Failed to create portal session");
  }
  return (await res.json()) as { url: string };
}
